import { FileClient } from "@formant/core/client/FileClient";
import { defined } from "@formant/core/common/defined";
import { ICloudFile } from "@formant/core/model/ICloudFile";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { StoreCache } from "./StoreCache";

export class CloudFileStore {
    private listCache = new StoreCache<undefined, ICloudFile[]>();
    private cache = new StoreCache<Uuid, ICloudFile | false>();

    constructor(
        private fileClient: FileClient,
        private authStore: IAuthStore,
    ) {}

    public async uploadFile(
        file: File,
        params?: { forceOverwrite?: boolean; contentType?: string },
    ) {
        await this.fileClient.uploadFile(
            await this.authStore.token(),
            file,
            params,
        );
        await this.fetchList();
    }

    public listAll(): ICloudFile[] | undefined {
        return this.listCache.get(
            undefined,
            async () => await this.fetchList(),
        );
    }

    public get(id: Uuid): ICloudFile | false | undefined {
        return this.cache.get(
            id,
            async () => (await this.fetchGet(id)) || false,
        );
    }

    public async getUrl(id: Uuid): Promise<string> {
        return await this.fileClient.getUrl(await this.authStore.token(), id);
    }

    public async update(id: Uuid, cloudFile: Partial<ICloudFile>) {
        const updated = await this.fileClient.update(
            await this.authStore.token(),
            id,
            cloudFile,
        );
        this.cache.set(id, updated);
        this.listCache.clear();
        return updated;
    }

    public async delete(id: Uuid) {
        this.cache.set(id, false);
        await this.fileClient.delete(await this.authStore.token(), id);
        await this.fetchList();
    }

    private async fetchGet(id: Uuid): Promise<ICloudFile | false> {
        const cloudFile = await this.fileClient.get(
            await this.authStore.token(),
            id,
        );
        const value = cloudFile || false;
        this.cache.set(id, value);
        return value;
    }

    private async fetchList(): Promise<ICloudFile[]> {
        const value = await this.fileClient.list(await this.authStore.token());
        value.forEach(_ => this.cache.set(defined(_.id), _));
        this.listCache.set(undefined, value);
        return value;
    }
}
